import React, { useState, useEffect } from "react";
import useThemeStore from "../store/themeStore";
import { isStandalone, isMobile, isIOS, isIOSChrome } from "../utils/pwaUtils";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const MobileAppPrompt: React.FC = () => {
  const theme = useThemeStore((state) => state.theme);
  const [showPrompt, setShowPrompt] = useState(false);
  const [iosPrompt, setIosPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const dismissed = localStorage.getItem("pwa_prompt_dismissed");
    const dismissedAt = localStorage.getItem("pwa_prompt_dismissed_at");

    if (dismissed && dismissedAt) {
      const daysSinceDismissed =
        (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        return;
      }
    }

    if (!theme?.pwaEnabled || isStandalone() || !isMobile()) {
      return;
    }

    // iOS doesn't fire beforeinstallprompt — show manual instructions instead
    if (isIOS()) {
      setIosPrompt(true);
      return;
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShowPrompt(true);
    };

    window.addEventListener("beforeinstallprompt", handler);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, [theme?.pwaEnabled]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === "accepted") {
        setShowPrompt(false);
        localStorage.setItem("pwa_installed", "true");
      } else {
        // User dismissed the native prompt — treat same as tapping "Later"
        setShowPrompt(false);
        localStorage.setItem("pwa_prompt_dismissed", "true");
        localStorage.setItem("pwa_prompt_dismissed_at", Date.now().toString());
      }
    } catch {
      // install prompt failed silently
    }

    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem("pwa_prompt_dismissed", "true");
    localStorage.setItem("pwa_prompt_dismissed_at", Date.now().toString());
  };

  const installBtnColor = theme?.buttonTxtColour || theme?.txtColor || "#1a1a2e";

  if (!theme) return null;

  if (iosPrompt && !showPrompt) {
    const iosMessage = isIOSChrome()
      ? "Install must be done in Safari (iOS limitation)"
      : 'Tap Share → "Add to Home Screen"';

    return (
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          padding: "16px",
          backgroundColor: theme.color1 || "#1a1a2e",
          color: theme.txtColor || "#ffffff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          zIndex: 9999,
          boxShadow: "0 -4px 20px rgba(0,0,0,0.15)",
          gap: "12px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px", flex: 1, minWidth: 0 }}>
          {theme.logo && (
            <img src={theme.logo} alt={theme.name} style={{ width: "44px", height: "44px", objectFit: "contain", borderRadius: "8px", flexShrink: 0 }} />
          )}
          <div style={{ minWidth: 0 }}>
            <strong style={{ display: "block", fontSize: "15px", marginBottom: "2px" }}>
              Install {theme.name}
            </strong>
            <p style={{ margin: 0, fontSize: "13px", opacity: 0.85 }}>{iosMessage}</p>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          style={{
            padding: "10px 16px",
            border: `1px solid ${theme.txtColor || "#ffffff"}`,
            background: "transparent",
            color: theme.txtColor || "#ffffff",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: 500,
            flexShrink: 0,
          }}
        >
          Later
        </button>
      </div>
    );
  }

  if (!showPrompt) return null;

  return (
    <div
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        padding: "16px",
        backgroundColor: theme.color1 || "#1a1a2e",
        color: theme.txtColor || "#ffffff",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        zIndex: 9999,
        boxShadow: "0 -4px 20px rgba(0,0,0,0.15)",
        gap: "12px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          flex: 1,
          minWidth: 0,
        }}
      >
        {theme.logo && (
          <img
            src={theme.logo}
            alt={theme.name}
            style={{
              width: "44px",
              height: "44px",
              objectFit: "contain",
              borderRadius: "8px",
              flexShrink: 0,
            }}
          />
        )}
        <div style={{ minWidth: 0 }}>
          <strong
            style={{
              display: "block",
              fontSize: "15px",
              marginBottom: "2px",
            }}
          >
            Install {theme.name}
          </strong>
          <p
            style={{
              margin: 0,
              fontSize: "13px",
              opacity: 0.85,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            Add to your home screen for quick access
          </p>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          gap: "8px",
          flexShrink: 0,
        }}
      >
        <button
          onClick={handleDismiss}
          style={{
            padding: "10px 16px",
            border: `1px solid ${theme.txtColor || "#ffffff"}`,
            background: "transparent",
            color: theme.txtColor || "#ffffff",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: 500,
          }}
        >
          Later
        </button>
        <button
          onClick={handleInstall}
          style={{
            padding: "10px 16px",
            border: "none",
            background: theme.color2 || theme.txtColor || "#ffffff",
            color: installBtnColor,
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: 600,
          }}
        >
          Install
        </button>
      </div>
    </div>
  );
};

export default MobileAppPrompt;
